/**
 * Dia do usuário, não dia UTC — invariante 16 do CLAUDE.md.
 *
 * O host roda em UTC e o usuário vive em America/Sao_Paulo. Uma sessão às 22h
 * em Natal é 01h do dia seguinte em UTC. Se a ofensiva contar o dia pelo prefixo
 * do texto ISO, essa sessão cai no dia errado e a sequência quebra em silêncio,
 * exatamente na hora em que a pessoa mais estuda.
 *
 * Nada aqui usa deslocamento fixo. O deslocamento é perguntado ao `Intl` para
 * cada instante, porque ele muda: o Brasil teve horário de verão até 2019 e
 * respostas importadas de um histórico antigo precisam cair no dia certo.
 */
const RE_DIA = /^\d{4}-\d{2}-\d{2}$/;
export function ehDiaCivil(valor) {
    return RE_DIA.test(valor) && !Number.isNaN(Date.parse(`${valor}T00:00:00Z`));
}
function exigirDia(dia) {
    if (!ehDiaCivil(dia)) {
        throw new TypeError(`Dia civil inválido: ${JSON.stringify(dia)}. Esperado AAAA-MM-DD.`);
    }
}
function exigirInstante(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        throw new TypeError(`Instante inválido: ${JSON.stringify(iso)}.`);
    }
    return d;
}
const cacheFormatadores = new Map();
function formatador(fuso) {
    let f = cacheFormatadores.get(fuso);
    if (!f) {
        // Lança RangeError se o fuso não existir — melhor descobrir na configuração
        // do que descobrir num heatmap torto seis meses depois.
        f = new Intl.DateTimeFormat("en-US", {
            timeZone: fuso,
            hour12: false,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        cacheFormatadores.set(fuso, f);
    }
    return f;
}
/** Que horas o relógio da parede marcava, naquele fuso, naquele instante. */
function paredeDoRelogio(instante, fuso) {
    const partes = formatador(fuso).formatToParts(instante);
    const pegar = (tipo) => {
        const p = partes.find((x) => x.type === tipo);
        if (!p)
            throw new Error(`Intl não devolveu a parte "${tipo}" para o fuso ${fuso}.`);
        return Number(p.value);
    };
    return {
        ano: pegar("year"),
        mes: pegar("month"),
        dia: pegar("day"),
        // Algumas plataformas devolvem "24" para a meia-noite em hour12:false.
        hora: pegar("hour") % 24,
        minuto: pegar("minute"),
        segundo: pegar("second"),
    };
}
/**
 * Deslocamento do fuso, em minutos, válido *naquele instante*.
 * Positivo a leste de Greenwich. America/Sao_Paulo hoje devolve -180.
 */
export function deslocamentoMinutos(instanteIso, fuso) {
    const instante = exigirInstante(instanteIso);
    const p = paredeDoRelogio(instante, fuso);
    const comoSeFosseUtc = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
    // Descarta os milissegundos dos dois lados: o Intl não os devolve.
    const real = Math.floor(instante.getTime() / 1000) * 1000;
    return Math.round((comoSeFosseUtc - real) / 60000);
}
/** A que dia do usuário este instante pertence. */
export function diaLocal(instanteIso, fuso) {
    const p = paredeDoRelogio(exigirInstante(instanteIso), fuso);
    const mm = String(p.mes).padStart(2, "0");
    const dd = String(p.dia).padStart(2, "0");
    return `${p.ano}-${mm}-${dd}`;
}
/**
 * O instante UTC em que este dia começa para o usuário.
 *
 * A meia-noite local é procurada em duas passadas porque o deslocamento pode
 * mudar entre o palpite e a resposta — é o que acontece na virada do horário
 * de verão. A segunda passada usa o deslocamento medido no próprio candidato.
 */
export function inicioDoDiaEmUtc(dia, fuso) {
    exigirDia(dia);
    const [ano, mes, d] = dia.split("-").map(Number);
    const meiaNoiteComoUtc = Date.UTC(ano, mes - 1, d, 0, 0, 0);
    let candidato = meiaNoiteComoUtc - deslocamentoMinutos(new Date(meiaNoiteComoUtc).toISOString(), fuso) * 60000;
    const desloc = deslocamentoMinutos(new Date(candidato).toISOString(), fuso);
    candidato = meiaNoiteComoUtc - desloc * 60000;
    // Em fuso que pula a meia-noite (existe: Cuiabá já pulou 00:00 na virada do
    // horário de verão), a meia-noite local não existe. O dia então começa no
    // primeiro instante que já pertence a ele.
    if (diaLocal(new Date(candidato).toISOString(), fuso) !== dia) {
        for (let m = 1; m <= 180; m++) {
            const tentativa = candidato + m * 60000;
            if (diaLocal(new Date(tentativa).toISOString(), fuso) === dia) {
                return new Date(tentativa).toISOString();
            }
        }
        throw new Error(`Não foi possível localizar o início de ${dia} no fuso ${fuso}.`);
    }
    return new Date(candidato).toISOString();
}
/**
 * O instante UTC em que este dia termina — exclusivo.
 * Use como `respondidoEm >= inicio AND respondidoEm < fim`, nunca `<=`.
 */
export function fimDoDiaEmUtc(dia, fuso) {
    return inicioDoDiaEmUtc(somarDias(dia, 1), fuso);
}
/** Faixa meio-aberta `[inicio, fim)` do dia, pronta para consulta. */
export function faixaDoDiaEmUtc(dia, fuso) {
    return { inicio: inicioDoDiaEmUtc(dia, fuso), fim: fimDoDiaEmUtc(dia, fuso) };
}
/** Soma dias civis sem passar por fuso nenhum — aritmética de calendário. */
export function somarDias(dia, quantidade) {
    exigirDia(dia);
    const t = Date.parse(`${dia}T00:00:00Z`) + quantidade * 86400000;
    return new Date(t).toISOString().slice(0, 10);
}
/** Diferença em dias civis: `depois - antes`. Usado pela regra da ofensiva. */
export function diasEntre(antes, depois) {
    exigirDia(antes);
    exigirDia(depois);
    return Math.round((Date.parse(`${depois}T00:00:00Z`) - Date.parse(`${antes}T00:00:00Z`)) / 86400000);
}
/** O dia do usuário agora. `agora` é injetável para o teste não depender do relógio. */
export function hojeDoUsuario(fuso, agora = new Date()) {
    return diaLocal(agora.toISOString(), fuso);
}
/** Instante atual no formato em que o banco guarda tudo: ISO 8601 UTC. */
export function agoraUtc(agora = new Date()) {
    return agora.toISOString();
}
/**
 * O dia da semana de um dia civil: 0 domingo … 6 sábado.
 *
 * Recebe `DiaCivil` de propósito, e não um instante. O dia civil **já é o dia
 * do usuário** — quem converteu o instante foi `diaLocal`, no fuso da
 * configuração. Daqui para a frente a conta é de calendário e não toca fuso
 * nenhum, que é o que a torna exata.
 *
 * O erro que isto existe para não deixar acontecer é o oposto: tirar o dia da
 * semana do instante UTC. Às 22h de domingo em São Paulo já é segunda em UTC, e
 * a regra de descanso da RN-17 passaria a valer três horas antes todo dia —
 * errando justamente a faixa da noite, que é quando se estuda.
 */
export function diaDaSemana(dia) {
    exigirDia(dia);
    return new Date(`${dia}T00:00:00Z`).getUTCDay();
}
//# sourceMappingURL=dia.js.map